import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  MapPin, Phone, Mail, Globe, Copy, Upload, FileText, Loader2, LogOut,
  Gift, Database, Trash2, CreditCard, ExternalLink, History,
} from "lucide-react";
import api, { API, TOKEN_KEY, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export default function Settings() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef();
  const [settings, setSettings] = useState(null);
  const [rib, setRib] = useState({ holder: "", iban: "", bic: "", bank: "" });
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    const [s, st, a] = await Promise.all([
      api.get("/admin/settings"), api.get("/admin/stats"), api.get("/admin/activity"),
    ]);
    setSettings(s.data);
    setRib({ holder: "", iban: "", bic: "", bank: "", ...s.data.rib });
    setStats(st.data);
    setActivity(a.data);
  };

  useEffect(() => { load(); }, []);

  const saveRib = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings/rib", rib);
      toast.success("RIB enregistré");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const upload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/admin/settings/rib-pdf", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("RIB PDF importé");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
    }
  };

  const copyIban = async () => {
    await navigator.clipboard.writeText(
      `Titulaire: ${rib.holder}\nIBAN: ${rib.iban}\nBIC: ${rib.bic}\nBanque: ${rib.bank}`
    );
    toast.success("RIB copié");
  };

  const seedDemo = async () => {
    const { data } = await api.post("/admin/seed-demo");
    toast[data.success ? "success" : "error"](data.message);
    load();
  };

  const resetData = async () => {
    await api.delete("/admin/reset-data");
    toast.success("Données effacées");
    load();
  };

  const viewPdf = async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    try {
      const res = await fetch(`${API}/admin/settings/rib-pdf/download`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      const url = URL.createObjectURL(await res.blob());
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      toast.error("Impossible d'ouvrir le RIB.");
    }
  };

  if (!settings)
    return <div className="flex justify-center pt-24"><Loader2 className="w-7 h-7 animate-spin text-[#581825]" /></div>;

  const cave = settings.cave;
  const used = stats ? stats.launchOffer : 0;
  const pct = Math.min(100, (used / 40) * 100);

  return (
    <div className="px-4 pt-6 space-y-6">
      <h1 className="font-serif text-3xl font-bold text-[#581825]">Réglages</h1>

      {/* Cave info */}
      <section className="bg-white rounded-2xl border border-[#E5DEC9] p-5">
        <h2 className="font-serif text-xl font-semibold text-[#581825] mb-3">{cave.name}</h2>
        <div className="space-y-2 text-sm text-[#6E675F]">
          <p className="flex items-center gap-2"><Phone className="w-4 h-4 text-[#581825]" /> {cave.phone}</p>
          <p className="flex items-center gap-2"><Mail className="w-4 h-4 text-[#581825]" /> {cave.email}</p>
          <p className="flex items-center gap-2"><MapPin className="w-4 h-4 text-[#581825]" /> {cave.address}</p>
          <p className="flex items-center gap-2"><Globe className="w-4 h-4 text-[#581825]" /> {cave.website}</p>
        </div>
      </section>

      {/* RIB */}
      <section className="bg-white rounded-2xl border border-[#E5DEC9] p-5" data-testid="settings-iban-display">
        <h2 className="flex items-center gap-2 font-serif text-xl font-semibold text-[#581825] mb-3">
          <CreditCard className="w-5 h-5" /> Coordonnées bancaires (RIB)
        </h2>
        <div className="space-y-3">
          <div><Label>Titulaire</Label><Input value={rib.holder} onChange={(e) => setRib({ ...rib, holder: e.target.value })} /></div>
          <div><Label>IBAN</Label><Input value={rib.iban} onChange={(e) => setRib({ ...rib, iban: e.target.value })} placeholder="FR76 ..." /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>BIC</Label><Input value={rib.bic} onChange={(e) => setRib({ ...rib, bic: e.target.value })} /></div>
            <div><Label>Banque</Label><Input value={rib.bank} onChange={(e) => setRib({ ...rib, bank: e.target.value })} /></div>
          </div>
        </div>
        <div className="flex gap-2 mt-4">
          <Button onClick={saveRib} disabled={saving} className="flex-1 bg-[#581825] hover:bg-[#42101A] text-[#FDFBF7]">
            {saving ? "..." : "Enregistrer"}
          </Button>
          <Button variant="outline" onClick={copyIban} data-testid="btn-copy-iban"><Copy className="w-4 h-4 mr-2" /> Copier</Button>
        </div>

        <div className="mt-4 pt-4 border-t border-[#E5DEC9]">
          <input ref={fileRef} type="file" accept="application/pdf" className="hidden" onChange={upload} />
          {settings.ribPdf ? (
            <div className="flex items-center gap-3 rounded-xl bg-[#F5EFEB] p-3">
              <FileText className="w-5 h-5 text-[#581825]" />
              <span className="flex-1 text-sm truncate text-[#110F0F]">{settings.ribPdf.filename}</span>
              <Button size="sm" variant="outline" onClick={viewPdf}><ExternalLink className="w-4 h-4 mr-1" /> Voir</Button>
              <Button size="sm" variant="ghost" onClick={() => fileRef.current.click()}>Remplacer</Button>
            </div>
          ) : (
            <Button variant="outline" onClick={() => fileRef.current.click()} disabled={uploading} className="w-full" data-testid="btn-upload-rib-pdf">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
              Importer un RIB en PDF
            </Button>
          )}
        </div>
      </section>

      {/* Launch offer quota */}
      <section className="bg-white rounded-2xl border border-[#E5DEC9] p-5">
        <h2 className="flex items-center gap-2 font-serif text-xl font-semibold text-[#581825] mb-3">
          <Gift className="w-5 h-5" /> Offre de lancement
        </h2>
        <p className="text-sm text-[#6E675F] mb-2">{used} / 40 premiers abonnés</p>
        <div className="h-3 rounded-full bg-[#F5EFEB] overflow-hidden">
          <div className="h-full bg-[#C5A059]" style={{ width: `${pct}%` }} />
        </div>
        <p className="text-xs text-[#6E675F] mt-2">
          {used >= 40 ? "Offre complète." : `Il reste ${40 - used} place${40 - used > 1 ? "s" : ""}.`}
        </p>
      </section>

      {/* Data tools */}
      <section className="bg-white rounded-2xl border border-[#E5DEC9] p-5">
        <h2 className="flex items-center gap-2 font-serif text-xl font-semibold text-[#581825] mb-3">
          <Database className="w-5 h-5" /> Données
        </h2>
        <div className="space-y-2">
          <Button variant="outline" onClick={seedDemo} className="w-full" data-testid="btn-seed-demo">
            Charger des données de démo
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="ghost" className="w-full text-red-600 hover:bg-red-50">
                <Trash2 className="w-4 h-4 mr-2" /> Effacer tous les abonnés
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="bg-[#FDFBF7]">
              <AlertDialogHeader>
                <AlertDialogTitle>Effacer toutes les données ?</AlertDialogTitle>
                <AlertDialogDescription>Tous les abonnés et échéances seront supprimés. Irréversible.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Annuler</AlertDialogCancel>
                <AlertDialogAction onClick={resetData} className="bg-red-600 hover:bg-red-700">Tout effacer</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </section>

      <section className="bg-white rounded-2xl border border-[#E5DEC9] p-5" data-testid="activity-log">
        <h2 className="flex items-center gap-2 font-serif text-xl font-semibold text-[#581825] mb-3">
          <History className="w-5 h-5" /> Journal d'activité
        </h2>
        {activity.length === 0 ? (
          <p className="text-sm text-[#6E675F]">Aucune activité pour l'instant.</p>
        ) : (
          <ul className="space-y-2 max-h-72 overflow-y-auto no-scrollbar">
            {activity.map((e, i) => {
              const dot = e.kind === "login_success" ? "bg-emerald-500"
                : e.kind === "login_failed" ? "bg-amber-500"
                : e.kind === "login_locked" || e.kind === "payment_blocked" ? "bg-red-500"
                : "bg-[#581825]";
              return (
                <li key={i} className="flex items-start gap-3 text-sm">
                  <span className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${dot}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-[#110F0F]">{e.message}</p>
                    <p className="text-xs text-[#6E675F]">
                      {new Date(e.at).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                    </p>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <Button variant="ghost" onClick={logout} className="w-full text-[#581825]" data-testid="btn-logout">
        <LogOut className="w-4 h-4 mr-2" /> Se déconnecter
      </Button>
    </div>
  );
}
